/*
 * Version of Statement specific to reseller
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid } from '@mui/material';
import { TablePagination, Typography, Table,  TableBody, TableCell, TableRow, Tooltip  } from '@mui/material';
import GetApp from '@mui/icons-material/GetApp';
import ResellerStatementLines from './ResellerStatementLines';
import StatementSelect from './StatementSelect';
import StatementSelectFetch from './StatementSelectFetch';
import ResellerStatementLinesFetch from './ResellerStatementLinesFetch';
import IntegrationCountFetch from './IntegrationCountFetch';
import HelpOutline from '@mui/icons-material/HelpOutline';

import LoadingSpinner from '../ui/LoadingSpinner';
import SavingSpinner from '../ui/SavingSpinner';
import ServerError from '../ui/ServerError';
import AppBindStyles from '../../AppBindStyles';
import { format } from 'date-fns';

const abEnvironment = new AbEnvironment();
const ResellerStatementQuery = graphql`
query ResellerStatementQuery($pAccountId: UUID, $startDate: Datetime, $endDate: Datetime, $first: Int, $offset: Int) {
  getresellerstatementlines2(paccountid: $pAccountId, startts: $startDate, endts: $endDate, first: $first, offset: $offset) {
    totalCount
    nodes {
      beginningBalance
      paymentIn
      receivedIn
      chargeOut
      payoutOut
      endingBalance
      transactionId
      transactionTime
      transactionType
      accountId
      accountName
      accountStatus
      customerStatus
      psType
      psCcBrand
      psCcLast4
      psAchAccountNum
      psInAmt
      psOutAmt
      psCFee
      psRFee
      paymentStatus
      paymentStatusReason
      bcId
      bcLast4
      customerAppId
      appId
      appName
      appSku
      appLogo
      bindCardCharge
      markup
      wholesaleMarkup
      appbindFee
      passThroughFee
      balance
      paidByBalance
      backupPayment
    }
  }
}
`;

const styleSheet = theme => ({
  applogo: {
    margin: 0,
    padding: 0,
    height: 27,
  },
  appButton: {
    marginTop: 0, // Adjust to align better with text
    paddingTop: 2,
    paddingBottom: 2,
    paddingRight: 16,
    paddingLeft: 16
  },
  applabellogo: {
    margin: 0,
    padding: 0,
    height: 27,
  },
  downloadButton: {
    marginTop: '0px' // Adjust to align better with text
    // marginLeft: theme.spacing(1),
    // marginTop: '20px',
    // marginBottom: theme.spacing(1),
  },
  dateField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginTop: 4,
    width: 180,
  },
  textField2: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginTop: 3,
    width: 160,
  },
  textFieldSearch: {
    marginRight: theme.spacing(1),
    marginTop: 3,
    marginLeft: 0,
    width: 300,
  },
  tblWidth: {
    maxWidth: 340,
  },
  tblCellWidth120: {
    width: 120,
    borderBottom: 0,
    padding: '2px 28px 2px 12px',
  },
  tblCellWidth80: {
      width: 60,
      borderBottom: 0,
      padding: '2px 8px 2px 8px',
  },
  tblCellTt: {
      width: 32,
      borderBottom: 0,
      padding: '2px 8px 2px 8px',
  }
});

class ResellerStatement extends Component {

  constructor(props) {
    super(props);

    this.state = {
      statementIndex: 0,
      startDate: null,
      endDate: null,
      siId: null,
      pAccountId: this.props.pAccountId,
      page: 0,
      rowsPerPage: 10,
      showButtonSpinner: false,
      appLogo: null,
      mountLoadComplete: false
    };  
  }

  /*
   * Need a mount time load in order to set the initial state of siId correctly.
   */
  componentDidMount() {
    // If there is an active FreshBooks integration, determine if this statement has had

    let theAppLogo = null;

    IntegrationCountFetch.fetch(abEnvironment.getUserAttribute('a_id'), "FreshBooks").toPromise()
    .then(icf => {
      let theAccount = icf.accountTByAccountId;
      if (theAccount != null && theAccount.accountDelegatedAuthsTsByAccountId != null && theAccount.accountDelegatedAuthsTsByAccountId.totalCount > 0) {
        // Get the logo
        let theNode = theAccount.accountDelegatedAuthsTsByAccountId.nodes[0];
        if (theNode.appIntegrationTByAppIntegrationId != null) {
          theAppLogo = theNode.appIntegrationTByAppIntegrationId.appLogo;
        }
        return StatementSelectFetch.fetch(abEnvironment.getUserAttribute('a_id'), abEnvironment.getUserAttribute('a_id')).toPromise();
      } else {
        return null;
      }
    })
    .then(ssf => {
      if (ssf != null) {
        let selectedIndex = 0;
        let selectedStart = null;
        let selectedEnd   = null;
  
        let savedStatementSearch = localStorage.getItem('lastStatement');
        if (savedStatementSearch) {
          let search = savedStatementSearch.split("|");
          selectedIndex = search[0];
        }
  
        let theSiId = null;
        let nodes = ssf.getstatementperiods2.nodes;
        if (selectedIndex < nodes.length) {
          theSiId = nodes[selectedIndex].siId;
          selectedStart = nodes[selectedIndex].startTs;
          selectedEnd   = nodes[selectedIndex].endTs;
        }

        this.setState({siId: theSiId, appLogo: theAppLogo, mountLoadComplete: true, startDate: selectedStart, endDate: selectedEnd});
      }
    })
    .catch(err => {
      // Nothing to do
    });
  }

  handleChange = name => event => {
    this.setState({
      [name]: event.target.value,
      page: 0,
    });
  };

  setStatementSelectState = (idx, startTs, endTs, siId) => {
    let statementStorage = idx + '|' + startTs + '|' + endTs;
    localStorage.setItem('lastStatement', statementStorage);
    this.setState({ statementIndex: idx, startDate: startTs, endDate: endTs, siId: siId, page: 0});
  }

  handleChangePage = (event, page) => {
    this.setState({page: page});
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value, page : 0});
  }

  // The download needs to do another query in case there is more data for
  // the range than is shown on the screen.
  /*
  downloadCsv(startDate, endDate, pAccountId, first, offset) { 
    alert('startDate is ' + startDate);
  }
  */
  downloadCsv(startDate, endDate, pAccountId, first, offset) { 

    const { logError } = this.props.auth;

    var endDateStr;
    if (endDate == null) {
      endDateStr = format(new Date(), 'yyyy-MM-dd');
    } else {
      endDateStr = format(new Date(endDate), 'yyyy-MM-dd');
    }
    ResellerStatementLinesFetch.fetch(pAccountId, startDate, endDate, first, offset).toPromise()
    .then(sl => {
      let theCount = sl.getresellerstatementlines2.totalCount;
      if (theCount < 1) {
        return;
      }
      let thenodes = sl.getresellerstatementlines2.nodes;
      let csv;

      csv = 'Date,Who,Item,Cost,Revenue,AppBind,CC,Payment,Funding,Payout,Balance\n';
      thenodes.map(n=> {
        let tdate = format(new Date(n.transactionTime), 'yyyy-MM-dd');
        let who = n.accountName;
        let app = '';
        if (n.appName) {
          app = n.appName;
        }

        let customerPay = false;
        // Some fields should only be populated if the reseller is paying.  These are:
        // - charge (the bindcard charge)
        // - psOutAmount (payment)
        if (n.psInAmt != null && n.psInAmt === n.bindCardCharge && n.accountId !== abEnvironment.getUserAttribute('a_id')) {
          customerPay = true;
        }

        let tType = n.transactionType;
        // let psInAmt = '';
        // let psOutAmt = '';
        let psNetworkFee = '';
        // let appName = null;
        // let appSku = null;
        let bindCardCharge = '';
        let markup = '';
        let appbindFee = '';
        let balance = '';

        if (tType === 15) {
          who = 'AppBind account credit';
        }

        if (tType === 16) {
          who = 'AppBind account debit';
        }

        if (tType === -1) { // Pending bindcard charge
          bindCardCharge = '(' + Number(n.bindCardCharge).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';

          // appName = n.appName;
        } else if (tType === 0 || tType === 99 || tType === 32) { // Bindcard charge or service or premium plan
          // If the customer is paying, we want the ui it put the cost in italics to signal that it doesn't impact the reseller balance.
          // There is a workaround to pass an amount in on psInAmt if the customer is paying.  So check this.
          if (n.psInAmt != null && n.psInAmt === n.bindCardCharge && n.accountId !== abEnvironment.getUserAttribute('a_id')) {
            customerPay = true;
          }

          if (!customerPay) {
            bindCardCharge = '(' + Number(n.bindCardCharge).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
          }

          // Markup should be blank if this is provisioner buying for self and markup is 0.
          if (n.accountId === abEnvironment.getUserAttribute('a_id') && (n.markup == null || n.markup === 0)) {
            markup = '';
          } else {

            let tmarkup = Number(n.markup);

            if (n.passThroughFee != null) {
              tmarkup = tmarkup + Number(n.passThroughFee);
            }

            if (n.wholesaleMarkup != null) {
              tmarkup = tmarkup + Number(n.wholesaleMarkup);
            }

            markup = Number(tmarkup).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            if (tmarkup < 0) {
              markup = Number(tmarkup * -1).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
              markup = '(' + markup + ')';
            }
          }

          appbindFee = '(' + Number(n.appbindFee).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';

          // appName = n.appName;
          // appSku = n.appSku;

        } else if (tType === 1 || tType === 3 || tType === 8 || tType === 31 || tType === 33) { // Funding source charge. 1 is normal, 3 is dunning, 8 is Topup, 31 is service payment, 33 is an appbind plan fee
        
          if (n.passThroughFee != null) {
            markup = Number(n.passThroughFee).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
          }

          // psOutAmt = Number(n.psOutAmt).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

          let ttlFee = Number(n.psCFee) + Number(n.psRFee);
          if (ttlFee === 0) {
            psNetworkFee = '';
          } else {
            psNetworkFee = '(' + Number(ttlFee).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
          }
        } else if (tType === 4 || tType === 11) { // Payout
          

          if (tType === 11 && n.psRFee != null && Number(n.psRFee) > 0) {
            psNetworkFee = '(' + Number(n.psRFee).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
 
          } else {
            psNetworkFee = '';
          }

          // psInAmt = '(' + Number(n.psInAmt).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
        } else if (tType === 6) {
          // refund = true;

          // If the customer paid, the refund is to the customer, and we want the ui it put the cost in italics to signal that it doesn't impact the reseller balance.
          // There is a workaround to pass an amount in on psInAmt if the customer is paying.  So check this.
          if (n.psInAmt != null && n.psInAmt === n.bindCardCharge && n.accountId !== abEnvironment.getUserAttribute('a_id')) {
            customerPay = true;
          }

          // There are two components to the refund.  The bindCardCharge holds the refund from the vendor,
          // and the markup holds the refund from the reseller
          if (!customerPay) {
            bindCardCharge = Number(n.bindCardCharge * -1).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
          }


          if (n.markup != null && n.markup < 0) {
            let tmarkup = Number(n.markup) * -1;
            markup = '(' + Number(tmarkup).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
          }
        } else  if (tType === 15) {
          //credit amount from appbind - does it make sense to list this as "appbindfee"?
          appbindFee = Number(n.markup).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        } else if (tType === 16) {
          //debit account from appbind
          let tmarkup = n.markup * -1;
          appbindFee = '(' + Number(tmarkup).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
        } else if (tType === 21 || tType === 23) {
          markup = Number(n.markup).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
          if (n.markup < 0) {
            markup = '(' + Number(n.markup * -1).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
          }
        }

        // Balance is on all
        if (n.balance < 0) {
          let negBalance = -1 * Number(n.balance);
          balance = '(' + Number(negBalance).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
        } else {
          balance = Number(n.balance).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        }

  
        let payout = '';
        if ((tType === 4 || tType === 11) && n.psInAmt) {
          let pout = n.psInAmt * -1;
          payout = Number(pout)
                  .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        }
        
        let source = '';
        if (n.psType != null) {
          if (n.psCcBrand) {
            source = 'CC ***' + n.psCcLast4;
          } else {
            // psSource = 'ACH ***' + n.psAchAccountNum;
            source = 'ACH';
          }
        }
        
        let payment = '';
        if (n.accountId === abEnvironment.getUserAttribute('a_id') && n.psOutAmt && n.psOutAmt !== 0) {
          payment = Number(n.psOutAmt)
          .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        }

        let csvline = '' + tdate + ',' + who + ','
                    + app + ',"' + bindCardCharge + '","' + markup  + '",' + appbindFee + ','
                    + psNetworkFee + ',"' + payment + '",'
                    + source + ',' + payout + ',"' + balance + '"\n';
        csv = csv + csvline;
        return true;
      });

      var downloadName = 'AppBind-Statement-' + endDateStr + '.csv';
      var hiddenElement = document.createElement('a');
      hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);
      hiddenElement.target = '_blank';
      hiddenElement.download = downloadName;
      hiddenElement.click(); 
    })
    .catch(err => {
      // alert('Error: ' + err);
      logError('Exception in ResellerStatement.js: ' + err);
    });
  }

  syncToAccounting() {
    this.setState({ showButtonSpinner: true });
    const { callPartnerApi } = this.props.auth;
    Promise.resolve()
    .then(() => {
      let payload = {
        operation: "FreshBooksLinkStatementToInvoice",
        params: {
          abCustomerAccountId: null,
          startTs: this.state.startDate,
          endTs: this.state.endDate
        }
      };
  
      return callPartnerApi('appbind/generic', 'POST', payload);
    })
    .then(response => {
      return response.json();
    })
    .then(jresp => {
      this.setState({ showButtonSpinner: false });
      window.location.reload();
    })
    .catch(err => {
      this.setState({ showButtonSpinner: false });
    });
  }

  render() {
    const { isAuthenticatedReseller, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    if (!isAuthenticatedReseller() || needAssent()) { 
      return ( <Redirect to='/home' /> );
    }    

    // Determine the offset
    let offset = 0;
    if (this.state.page > 0)
    {
      offset = this.state.page * this.state.rowsPerPage
    }
    let first = this.state.rowsPerPage;

    let totalCount = 0;

    let selectAccountId = this.state.pAccountId;

    let selectedIndex = this.state.statementIndex;
    let selectedStart = this.state.startDate;
    let selectedEnd = this.state.endDate;

    let savedStatementSearch = localStorage.getItem('lastStatement');
    if (savedStatementSearch) {
      let search = savedStatementSearch.split("|");
      selectedIndex = search[0];
      selectedStart = search[1];
      selectedEnd = search[2];
    }

    let showDownloadButton = true;
    let showFbSyncButton = false;
    let showFbSyncButtonDisabled = false;
    let showFbSyncLabel = false;
    // Mount load complete is only set to true if the mount is complete and there was FreshBooks integration information.
    if (this.state.mountLoadComplete === true) {
      // Is this statement the current period, and therefore incomplete and not ready for integration
      if (selectedEnd != null) {
        // Check the current time
        let nowDate = new Date();
        let endDate = new Date(selectedEnd);
        if (nowDate.getTime() > endDate.getTime()) {          
          if (this.state.siId == null) {
            showFbSyncButton = true;
          } else {
            showFbSyncLabel = true;
          }
        } else {
          if (this.state.siId == null) {
            showFbSyncButtonDisabled = true;
          }
        }
      }
    }

    // TODO: switch to use of browser timezone (evenually)
    // let startUtc = new Date(selectedStart).toISOString();
    // let endUtc = new Date(selectedEnd).toISOString();

    return (
      <React.Fragment>
        <Grid container spacing={2} direction="row" justifyContent="space-between" alignItems="center">
          <Grid item>
            <StatementSelect
              setStatementSelectState={this.setStatementSelectState}
              accountId={selectAccountId}
              statementIndex={selectedIndex}
              widthOverride={270}
            />
          </Grid>
          <Grid item>
            <Grid container spacing={2} alignItems="center">
            { showFbSyncButton && ! this.state.showButtonSpinner &&
              <Grid item>
                <Button
                  color="primary"
                  variant="outlined"
                  className={classes.appButton}
                  onClick={() => 
                    this.syncToAccounting()
                  }
                >
                  Send expenses to &nbsp;
                  <img
                    alt="App"
                    src={this.state.appLogo}
                    className={classes.applogo}
                  />                  
                </Button>
              </Grid>}
              { showFbSyncButton && this.state.showButtonSpinner &&
              <Grid item>
                <Button
                  color="primary"
                  variant="outlined"
                >
                  <SavingSpinner/>
                </Button>
              </Grid>}
              { showFbSyncButtonDisabled && 
              <Grid item>
                <Tooltip title="You'll be able send expenses into FreshBooks for this statement at the end of the period.">
                  <div>
                    <Button
                      color="primary"
                      variant="outlined"
                      disabled={true}
                      className={classes.appButton}
                      
                    >
                      Send expenses to &nbsp;
                      <img
                        alt="App"
                        src={this.state.appLogo}
                        className={classes.applogo}
                      />                  
                    </Button>
                    </div>
                </Tooltip>
              </Grid>}
              { showFbSyncLabel &&
              <Grid item>
                <Tooltip title='Expenses in this statement have been sent to FreshBooks'>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      <Typography color="primary" variant="button">Sent to</Typography>
                    </Grid>
                    <Grid item>
                      <img
                        alt="App"
                        src={this.state.appLogo}
                        className={classes.applabellogo}
                      />
                    </Grid>
                  </Grid>
                </Tooltip>
              </Grid>}
              { showDownloadButton &&
              <Grid item>
                <Button
                  color="primary"
                  variant="outlined"
                  className={classes.downloadButton}
                  onClick={() => 
                    this.downloadCsv(selectedStart, selectedEnd, this.state.pAccountId, 10000, 0)
                  }
                >
                  Download Statement&nbsp;&nbsp;<GetApp/>
                </Button>
              </Grid>}
            </Grid>
          </Grid>
        </Grid>
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={ResellerStatementQuery}
          variables={{pAccountId: this.state.pAccountId,
                      startDate: selectedStart,
                      endDate: selectedEnd,
                      first: first,
                      offset: offset}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              // Determine offset information
              totalCount = props.getresellerstatementlines2.totalCount;

              let beginningBalance = 0;
              let paymentIn = 0;
              let receivedIn = 0;
              let chargeOut = 0;
              let payoutOut = 0;
              let endingBalance = 0;
              if (totalCount > 0) {
                let firstNode = props.getresellerstatementlines2.nodes[0];

                if (firstNode.beginningBalance)
                  beginningBalance = firstNode.beginningBalance;
                if (firstNode.paymentIn)
                  paymentIn = firstNode.paymentIn;
                if (firstNode.receivedIn)
                  receivedIn = firstNode.receivedIn;
                if (firstNode.chargeOut)
                  chargeOut = firstNode.chargeOut;
                if (firstNode.payoutOut)
                  payoutOut = firstNode.payoutOut;
                if (firstNode.endingBalance)
                  endingBalance = firstNode.endingBalance;
              }
              let beginningBalanceF = Number(beginningBalance).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
              let paymentInF        = Number(paymentIn).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
              let receivedInF       = Number(receivedIn).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
              let chargeOutF        = Number(chargeOut).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
              let payoutOutF        = Number(payoutOut).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
              let endingBalanceF    = Number(endingBalance).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

              return (
                <Grid container spacing={1} direction="row" justifyContent="flex-start">
                  <Grid item xs={12}>
                    <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                      Statement summary
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Table className={classes.tblWidth}>
                      <TableBody>
                        <TableRow>
                          <TableCell className={classes.tblCellWidth120}><Typography variant="body1">Beginning balance</Typography></TableCell>
                          <TableCell align="right" className={classes.tblCellWidth80}><Typography variant="body1">{beginningBalanceF}</Typography></TableCell>
                          <TableCell className={classes.tblCellTt}>
                            <Tooltip placement='right' title='Your AppBind balance is the amount available in AppBind in your account to purchase subscriptions or be paid out. This is your balance at the beginning of the statement period.'>
                              <HelpOutline fontSize="small" style={{verticalAlign: "-8%"}}/>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.tblCellWidth120}><Typography variant="body1">Payments</Typography></TableCell>
                          <TableCell align="right" className={classes.tblCellWidth80}><Typography variant="body1">{paymentInF}</Typography></TableCell>
                          <TableCell className={classes.tblCellTt}>
                            <Tooltip placement='right' title='This is the amount of payments that you have made to AppBind to cover fees and subscription costs.'>
                              <HelpOutline fontSize="small" style={{verticalAlign: "-8%"}}/>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.tblCellWidth120}><Typography variant="body1">Received</Typography></TableCell>
                          <TableCell align="right" className={classes.tblCellWidth80}><Typography variant="body1">{receivedInF}</Typography></TableCell>
                          <TableCell className={classes.tblCellTt}>
                            <Tooltip placement='right' title='This is the revenue you received as markup and service fees from your customers, and incentives and refunds from vendors.'>
                              <HelpOutline fontSize="small" style={{verticalAlign: "-8%"}}/>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.tblCellWidth120}><Typography variant="body1">Charges</Typography></TableCell>
                          <TableCell align="right" className={classes.tblCellWidth80}><Typography variant="body1">{chargeOutF}</Typography></TableCell>
                          <TableCell className={classes.tblCellTt}>
                            <Tooltip placement='right' title='This is the amount you have paid for subscriptions and fees.'>
                              <HelpOutline fontSize="small" style={{verticalAlign: "-8%"}}/>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.tblCellWidth120}><Typography variant="body1">Payouts</Typography></TableCell>
                          <TableCell align="right" className={classes.tblCellWidth80}><Typography variant="body1">{payoutOutF}</Typography></TableCell>
                          <TableCell className={classes.tblCellTt}>
                            <Tooltip placement='right' title='This is the amount paid out to you from AppBind.'>
                              <HelpOutline fontSize="small" style={{verticalAlign: "-8%"}}/>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.tblCellWidth120}><Typography variant="body1">Ending balance</Typography></TableCell>
                          <TableCell align="right" className={classes.tblCellWidth80}><Typography variant="body1">{endingBalanceF}</Typography></TableCell>
                          <TableCell className={classes.tblCellTt}>
                            <Tooltip placement='right' title='This is your balance at the at the end of the statement period.'>
                              <HelpOutline fontSize="small" style={{verticalAlign: "-8%"}}/>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                      Transactions
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <ResellerStatementLines cl={props.getresellerstatementlines2} history={this.props.history}/>
                  </Grid>
                  <Grid item xs={12}>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      component="div"
                      count={totalCount}
                      rowsPerPage={this.state.rowsPerPage}
                      page={this.state.page}
                      backIconButtonProps={{
                        'aria-label': 'Previous Page',
                      }}
                      nextIconButtonProps={{
                        'aria-label': 'Next Page',
                      }}
                      onPageChange={this.handleChangePage}
                      onRowsPerPageChange={this.handleChangeRowsPerPage}
                    />                        
                  </Grid>
                </Grid>
              )
            }
            return (
              <LoadingSpinner />
            );
          }}
        />
      </React.Fragment>
    );
  }
}
  
ResellerStatement.propTypes = {
  classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(ResellerStatement);
